/**
 * Every tunable value in the simulation. Nothing inside a move module carries a
 * magic number; it reads from here. The panel edits a Tuning object live, and
 * compileTuning() derives the tick and radian forms that step() reads.
 *
 * Every value is a number so the object can be hashed, diffed, saved, and
 * driven by sliders without special cases.
 */

import { DEG } from "./fastmath";
import { HZ } from "./types";

export interface Tuning {
  // Run
  /** Top ground speed, m/s. */
  maxRunSpeed: number;
  /** Ground acceleration toward the stick, m/s^2. */
  runAccel: number;
  /** Velocity applied on the first tick of input from rest, m/s. */
  startImpulse: number;
  /** Deceleration when the stick is released, m/s^2. */
  stopDecel: number;
  /** Deceleration when the stick is reversed, m/s^2. */
  turnDecel: number;
  /** Speed above which a stop or reversal enters the Skid state, m/s. */
  skidSpeed: number;
  /** Deceleration applied to ground speed above maxRunSpeed, m/s^2. */
  overspeedDecel: number;
  /** Fraction added to runAccel at full forward tilt. */
  leanAccelBonus: number;
  /** Fraction added to stopDecel at full back tilt. */
  leanBrakeBonus: number;

  // Attitude: the torso as a rotating body (v1 plan section 6.1)
  /** Moment of inertia standing with the arms in, normalized. */
  inertiaBase: number;
  /** Inertia multiplier at full tuck. */
  inertiaTucked: number;
  /** Inertia multiplier at full reach. */
  inertiaArmsOut: number;
  /** Angular acceleration at full tilt and inertiaBase, rad/s^2. */
  tiltTorque: number;
  /** Rotational drag in the air, 1/s. */
  spinDrag: number;
  /** Rotational drag on the ground, 1/s; about 0.7 of critical for rightingGround, so a lean settles fast with under a degree of overshoot. */
  spinDragGround: number;
  /** Righting spring on the ground, rad/s^2 per radian at inertiaBase (full tilt settles near 20 degrees at 90). */
  rightingGround: number;
  /** Righting spring in the air, rad/s^2 per radian at inertiaBase; 0 is free rotation (v4 flips). */
  rightingAir: number;
  /** Pump lean of the body off the arm line at full tilt while attached, degrees. */
  barLeanClamp: number;
  /** Time for the bar lean to go from none to full, ms. */
  barLeanMs: number;
  /** Fraction of the body's rotation on the bar carried into the flight by a release. */
  releaseSpin: number;
  /** Forward pitch rate added at touchdown per m/s of impact speed, rad/s per m/s. */
  impactPitch: number;

  // Crouch
  /** Ground speed cap at full crouch, m/s. */
  crouchSpeed: number;
  /** Time for tuckAmount to go 0 -> 1 while Tuck is held, ms. */
  tuckInMs: number;
  /** Time for tuckAmount to go 1 -> 0 after Tuck is released, ms. */
  tuckOutMs: number;

  // Jump
  gravity: number;
  gravityRiseScale: number;
  gravityFallScale: number;
  terminalVelocity: number;
  jumpVelocity: number;
  /** Multiplier applied to upward velocity when Jump is released while rising. */
  jumpCut: number;
  coyoteMs: number;
  bufferMs: number;
  /** Sideways nudge allowed when the top of the capsule clips a corner while rising, m. */
  cornerCorrection: number;
  airAccel: number;
  /** Deceleration applied to air speed above maxRunSpeed, m/s^2. */
  airDrag: number;

  // Body geometry
  capsuleRadius: number;
  standHeight: number;
  /** How much the capsule shrinks at full tuck, m. */
  crouchDrop: number;
  /** Shoulder height below the top of the capsule, m. */
  shoulderBelowTop: number;
  /**
   * How far the center of mass moves toward the feet at full tuck, m: the mass sits
   * standHeight / 2 - tuckComDrop * tuck above the feet (knees to the chest at 0.5). On the ground the
   * feet hold still and the mass drops; in the air and on a bar the mass holds and the feet come up.
   */
  tuckComDrop: number;

  // Arms
  armLength: number;
  /** Center of mass below the shoulders with the legs extended, m. */
  torsoOffset: number;
  /** Center of mass below the shoulders at full tuck, m. Smaller means a stronger tuck pump. */
  torsoOffsetTucked: number;
  /** Default reach direction above horizontal, degrees. */
  reachDirection: number;
  /** Reach amount at or above which the arms count as out. */
  gripThreshold: number;
  /**
   * Reach amount below which a held grip lets go (a plain release). Equal to gripThreshold it
   * is the same edge both ways; higher drops the bar earlier in the trigger's return travel.
   * A grip also needs at least this much reach to attach.
   */
  gripRelease: number;
  reachRadiusBase: number;
  reachRadiusPerReach: number;
  /** 1 = grip assist on (Standard scheme), 0 = off (Ninja). */
  gripAssist: number;
  gripAssistCone: number;
  gripAssistRange: number;

  // Swing
  catchRetention: number;
  attachTicks: number;
  hangOmega: number;
  swingGravityScale: number;
  swingDamping: number;
  /** Damping the swing ramps up to when nothing is pushed, tucked, or pulled on the bar, 1/s. */
  swingIdleDamping: number;
  /** Quiet time on the bar before the idle damping starts ramping in, ms; it is fully on after twice this. */
  swingIdleAfterMs: number;
  pumpGain: number;
  /**
   * Which stick pumps the swing while attached: 0 the right stick (torso tilt, the design's
   * mapping), 1 the left stick, 2 either (the two add and saturate at a full push; the owner's
   * choice, 2026-09-14).
   */
  pumpStick: number;
  pumpPerfectMs: number;
  pumpGoodMs: number;
  pumpBonus: number;
  /**
   * Swing amplitude that pumping cannot push past, degrees from straight down (130 since 2026-09-14,
   * so an over-pumped swing overshoots a 3 m bar but pumping alone never goes over the top). Tilt and tuck
   * pumps saturate here, and a catch that arrives with more is trimmed to it (2026-09-14).
   */
  maxSwingAngle: number;
  /** Added along the tangent by a Jump release, m/s. 0 since 2026-09-14: the swing alone propels, by the owner. */
  releaseKick: number;
  releaseKickTucked: number;
  releaseNudge: number;
  regrabMs: number;

  // Landing: roll and hard landing (v1 plan section 7.6)
  /** Forward spin at touchdown the legs absorb with the legs down, rad/s; more and the body rolls. */
  rollSpinUntucked: number;
  /** The same at full tuck, rad/s (a tucked body rolls with half the spin). */
  rollSpinTucked: number;
  /** A rolling body turns on a radius of the mass height plus this, m (a curled roll at 9 m/s turns in about 0.45 s). */
  rollRadius: number;
  /** How quickly a rolling body's spin locks to its speed over the rolling radius, 1/s. */
  rollGrip: number;
  /** The slowest a roll turns, rad/s, so a roll with little speed still comes round. */
  rollMinSpin: number;
  /** The last part of the turn over which the rolling spin eases out so the feet come under, degrees. */
  rollFinishDeg: number;
  /** A fall of the mass at least this far that neither rolls is a hard landing, m. */
  rollDropHeight: number;
  /** Horizontal speed kept by a hard landing. */
  hardLandingRetain: number;
  /** Roll PERFECT band: forward pitch at touchdown, degrees. */
  rollPerfectMin: number;
  rollPerfectMax: number;
  /** Roll GOOD band, degrees. */
  rollGoodMin: number;
  rollGoodMax: number;
  /** Speed a PERFECT roll adds, m/s. */
  rollPerfectBoost: number;

  // Hurdles: the hand plant, the vault, the mount, the step-over, the hit (v1 plan section 7.4)
  /** PERFECT vaults deliver their full push and slides carry speed without hit-stop; 0 replays older physics. */
  hurdleFlow: number;
  /** A box at most this tall above the feet is stepped over without a plant, m. */
  hurdleStepHeight: number;
  /** A box at most this tall is planted on; taller is a wall, m. */
  hurdleMaxHeight: number;
  /** How far ahead a box or pipe is looked for, m. */
  vaultLookahead: number;
  /**
   * Vault GOOD window: a Tuck press up to this long before the takeoff point, ms. The takeoff point is
   * where the flight's apex carries the hips vaultHipClearance over the top at the box's face; with no
   * press the takeoff there is automatic (OK).
   */
  vaultWindowMs: number;
  /** Vault PERFECT: a press vaultPerfectLeadMs before the takeoff point, plus or minus vaultPerfectHalfMs. */
  vaultPerfectLeadMs: number;
  vaultPerfectHalfMs: number;
  /** A hop over a knee-high box lifts the tucked feet this far over its top, m. */
  vaultClearance: number;
  /** A vault carries the hips (the mass) this far over the box top while the hands hold it, m. */
  vaultHipClearance: number;
  /** The hands rest on the box top this far ahead of the mass, m, and this far above the surface (the fist's radius). */
  vaultHandAhead: number;
  handOnTop: number;
  /** Mounting, the hands press the body up at this speed, m/s. */
  vaultMountPush: number;
  /** Cap on a late takeoff's vertical speed, m/s. */
  vaultPushMax: number;
  /** How long the hands can hold the body over the box; a body that cannot clear it in this time mounts, ms. */
  vaultSupportMs: number;
  /** Tuck the legs come up to while the hands are planted. */
  vaultTuck: number;
  /** Horizontal speed kept at GOOD and at OK; PERFECT keeps all of it. */
  vaultRetainGood: number;
  vaultRetainOk: number;
  /** Time over which the speed eases to the grade's retention while planted, ms. */
  vaultRetainMs: number;
  /** Speed a PERFECT vault adds while the hands push, m/s. */
  vaultPerfectBoost: number;
  /** Running into a box or pipe face at least this fast is a hit, m/s. */
  hitSpeed: number;

  // Slide (v1 plan section 7.5)
  /** A tuck on the ground at or above this speed slides, m/s. */
  slideSpeed: number;
  /** Slide deceleration, m/s^2. */
  slideDecel: number;
  /** Fraction added to slideDecel at full back tilt (the power slide). */
  slideBrakeBonus: number;
  /** Jump velocity out of a slide (lower and longer than a jump), m/s. */
  slideJumpVelocity: number;
  /** Slide GOOD window before a pipe's face, back to slideWindowLateMs, ms. */
  slideWindowMs: number;
  slideWindowLateMs: number;
  /** Slide PERFECT: slidePerfectMs before the face, plus or minus slidePerfectHalfMs. */
  slidePerfectMs: number;
  slidePerfectHalfMs: number;
  /** Speed a PERFECT slide adds once per pipe approach, m/s; 0 preserves legacy replay physics. */
  slidePerfectBoost: number;

  // Link (v1 plan section 8)
  /** Longest flight from a release to a catch that still counts as bar to bar, ms. */
  linkFlightMaxMs: number;
  /** A swing slower than hangOmega for this long is a dead hang and breaks the run, ms. */
  hangBreakMs: number;

  // Grades (v1 plan section 7)
  /** Catch GOOD needs the arms out at least this long before contact, ms. */
  catchSetupMs: number;
  /** Catch PERFECT also needs the mass at least this far behind the bar at the grab, degrees. */
  catchReadyDeg: number;
  /** Release PERFECT band, degrees past the bottom on the forward swing. */
  releasePerfectMin: number;
  releasePerfectMax: number;
  /** Release GOOD band, degrees past the bottom on the forward swing. */
  releaseGoodMin: number;
  releaseGoodMax: number;
  /** Release kick scale at PERFECT (GOOD is 1). */
  kickPerfectScale: number;
  /** Release kick scale at OK. */
  kickOkScale: number;
  /** The pump grade is centered this long before the bottom crossing, where the shortening's midpoint crosses it, ms. */
  pumpLeadMs: number;

  // Salmon ladder: a PERFECT duck near the bottom raises the bar through one animated rung.
  /** 1 requires PERFECT duck timing; 0 preserves the original apex pull/kip for saved tapes. */
  ladderPerfectDuck: number;
  /** Minimum time for the bar and attached body to rise one rung, ms. */
  ladderClimbMs: number;
  /** Maximum average upward speed; wider rung spacing takes longer, m/s (0 disables the limit). */
  ladderClimbSpeed: number;
  /** Pull amount at or above which a pull press counts. */
  ladderPullThreshold: number;
  /** A tuck let go counts as a kip only from at least this much tuck. */
  ladderKipTuck: number;
  /** PERFECT within this many ms either side of the apex; GOOD from this long before to this long after. */
  ladderPopPerfectMs: number;
  ladderPopEarlyMs: number;
  ladderPopLateMs: number;
  /** A swing narrower than this cannot lift the bar: the attempt is a miss, degrees of amplitude. */
  ladderMinAmplitude: number;
  /** Upward velocity the pull-up adds at GOOD, m/s, and its scale at PERFECT. */
  ladderPopLift: number;
  ladderPopPerfectScale: number;
  /** The pull-up eases the body up to the new rung over this long, ms. */
  ladderPopMs: number;
  /** A missed pop clips the bar on the rung: the swing keeps this much of its rate (1 keeps it all). */
  ladderMissRetention: number;
  /** 1: a missed pop drops the bar off the pegs and the player falls (the design's rule); 0: a clip only. */
  ladderMissFalls: number;

  // Ledges (DESIGN.md 4.9): the grab of a solid's top corner from the air, the hang, and the mantle
  /** The hanging body's side sits this far off the wall face, m. */
  ledgeGap: number;
  /** The speed the arms pull the body up the face from a hang, m/s (fast: a still hang has the arms unloaded). */
  mantlePullSpeed: number;
  /** The pull eases off so the feet come over the edge by this much before they come down on it, m. */
  mantleClearance: number;
  /** A mantle pressed within this long after the grab is PERFECT, ms (a press buffered before the grab is 0). */
  mantlePerfectMs: number;
  /** A mantle pressed within this long after the grab is GOOD, ms; later is OK. */
  mantleGoodMs: number;
  /** Horizontal speed from the grab kept over the edge at GOOD and at OK; PERFECT keeps all of it. */
  mantleRetainGood: number;
  mantleRetainOk: number;
  /** Speed a PERFECT mantle adds over the edge, m/s. */
  mantlePerfectBoost: number;
  /** The least speed a mantle comes over the edge with (a mantle from a dead hang), m/s. */
  mantleExitSpeed: number;
  /** Whole-world freeze on a PERFECT grade and on a new best link run, ticks; 0 disables. */
  hitStopTicks: number;

  // Input (applied by the input package before the frame reaches the sim)
  stickDeadzone: number;
  triggerDeadzone: number;
  /** Exponent of the trigger-to-reach curve; 1 is linear, 3.15 makes a 40% press an 80% reach. */
  reachCurve: number;
  /** Left stick (or d-pad, or W/S) up past this jumps and down past it tucks; 0 disables. */
  stickActions: number;
  stickActionThreshold: number;
  /**
   * Which pad scheme the bumpers, the left trigger, the right stick's vertical axis, and the left
   * stick's own up and down follow (an index into PAD_SCHEMES in the input package): 0 design,
   * bumpers unused; 1 LB jumps and RB tucks; 2 LB tucks and RB jumps; 3 scheme 1 plus right stick up
   * reaches and down tucks; 4 "body halves": LB jumps, LT tucks, right stick up reaches and down
   * pulls, RB pulls; 5 the owner's 2026-09-14 pick, scheme 4 without the stick reach; 6 the owner's
   * 2026-09-18 pick and the default: the right face button is the only crouch and the left trigger
   * held turns the left stick into the torso. See docs/controls.md.
   */
  padScheme: number;
  /** Trigger value past which a trigger used as a button or a modifier (LT in schemes 4 to 6) counts as pressed. */
  triggerActionThreshold: number;
  /**
   * A left stick deflected to at least this magnitude counts as a full run in its horizontal
   * direction, so a diagonal push for a stick jump or tuck does not bleed run speed; 0 disables.
   */
  stickRunAssist: number;
  /** The run assist needs at least this much horizontal deflection, so a near-vertical stick still stops. */
  stickRunAssistMinX: number;
  /**
   * On a bar a stick jump (a release) counts only when the stick passes the action threshold within
   * this many degrees of straight up: the stick's horizontal axis pumps the swing, and sweeping it
   * across the top of its gate must not let go of the bar. The keyboard and the buttons still release.
   */
  barStickJumpDeg: number;

  // Failure
  failPauseMs: number;
}

export const defaultTuning: Tuning = {
  maxRunSpeed: 9,
  runAccel: 26,
  startImpulse: 1.5,
  stopDecel: 70,
  turnDecel: 90,
  skidSpeed: 4,
  overspeedDecel: 6,
  leanAccelBonus: 0.15,
  leanBrakeBonus: 0.3,

  inertiaBase: 1.0,
  inertiaTucked: 0.5,
  inertiaArmsOut: 1.3,
  tiltTorque: 30,
  spinDrag: 3,
  spinDragGround: 14,
  rightingGround: 90,
  rightingAir: 12,
  barLeanClamp: 15,
  barLeanMs: 100,
  // 0.25 until 2026-09-27: a flight let go with the stick forward and the right stick untouched
  // rotated back and the hands trailed, and a 3 m link needed the dive as well (docs/v1-plan.md 17.2).
  releaseSpin: 0.1,
  impactPitch: 0.15,

  crouchSpeed: 3.5,
  tuckInMs: 100,
  tuckOutMs: 80,

  gravity: 25,
  gravityRiseScale: 0.9,
  gravityFallScale: 1.2,
  terminalVelocity: 30,
  jumpVelocity: 9.5,
  jumpCut: 0.45,
  coyoteMs: 100,
  bufferMs: 120,
  cornerCorrection: 0.15,
  // 12 until 2026-09-27: the stick held forward through a flight stretches a short one onto the bar.
  airAccel: 16,
  airDrag: 2,

  capsuleRadius: 0.3,
  standHeight: 1.8,
  crouchDrop: 0.6,
  shoulderBelowTop: 0.35,
  tuckComDrop: 0.5,

  armLength: 0.6,
  torsoOffset: 0.5,
  torsoOffsetTucked: 0.2,
  reachDirection: 60,
  gripThreshold: 0.15,
  gripRelease: 0.5,
  reachRadiusBase: 0.15,
  // 0.25 until 2026-09-27: the hands take a bar from 0.5 m at full reach now, 0.4 before.
  reachRadiusPerReach: 0.35,
  gripAssist: 1,
  gripAssistCone: 90,
  gripAssistRange: 2,

  catchRetention: 0.95,
  attachTicks: 6,
  hangOmega: 0.5,
  swingGravityScale: 1.0,
  swingDamping: 0.1,
  swingIdleDamping: 5,
  swingIdleAfterMs: 900,
  pumpGain: 2.0,
  pumpStick: 2,
  pumpPerfectMs: 40,
  pumpGoodMs: 100,
  pumpBonus: 1.08,
  maxSwingAngle: 130,
  releaseKick: 0,
  releaseKickTucked: 0,
  releaseNudge: 0.5,
  regrabMs: 200,

  rollSpinUntucked: 10,
  rollSpinTucked: 5,
  rollRadius: 0.25,
  rollGrip: 25,
  rollMinSpin: 4,
  rollFinishDeg: 60,
  rollDropHeight: 2.2,
  hardLandingRetain: 0.6,
  rollPerfectMin: 45,
  rollPerfectMax: 75,
  rollGoodMin: 30,
  rollGoodMax: 90,
  rollPerfectBoost: 0.3,

  hurdleFlow: 1,
  hurdleStepHeight: 0.5,
  hurdleMaxHeight: 1.3,
  vaultLookahead: 5,
  vaultWindowMs: 200,
  vaultPerfectLeadMs: 60,
  vaultPerfectHalfMs: 30,
  vaultClearance: 0.15,
  vaultHipClearance: 0.2,
  vaultHandAhead: 0.35,
  handOnTop: 0.07,
  vaultMountPush: 2.5,
  vaultPushMax: 9,
  vaultSupportMs: 220,
  vaultTuck: 0.8,
  vaultRetainGood: 0.9,
  vaultRetainOk: 0.75,
  vaultRetainMs: 200,
  vaultPerfectBoost: 2,
  hitSpeed: 2,

  slideSpeed: 5,
  slideDecel: 6,
  slideBrakeBonus: 1,
  slideJumpVelocity: 7,
  slideWindowMs: 320,
  slideWindowLateMs: 80,
  slidePerfectMs: 180,
  slidePerfectHalfMs: 40,
  slidePerfectBoost: 2,

  linkFlightMaxMs: 2000,
  hangBreakMs: 300,

  catchSetupMs: 40,
  catchReadyDeg: 25,
  releasePerfectMin: 35,
  releasePerfectMax: 45,
  releaseGoodMin: 25,
  releaseGoodMax: 55,
  kickPerfectScale: 1.2,
  kickOkScale: 0.5,
  pumpLeadMs: 50,
  ladderPerfectDuck: 1,
  ladderClimbMs: 250,
  ladderClimbSpeed: 1.8,
  ladderPullThreshold: 0.5,
  ladderKipTuck: 0.5,
  ladderPopPerfectMs: 60,
  ladderPopEarlyMs: 220,
  ladderPopLateMs: 140,
  ladderMinAmplitude: 30,
  ladderPopLift: 1.5,
  ladderPopPerfectScale: 1.3,
  ladderPopMs: 150,
  ladderMissRetention: 0.6,
  ladderMissFalls: 0,
  ledgeGap: 0.02,
  mantlePullSpeed: 4.5,
  mantleClearance: 0.05,
  mantlePerfectMs: 120,
  mantleGoodMs: 400,
  mantleRetainGood: 0.9,
  mantleRetainOk: 0.75,
  mantlePerfectBoost: 1,
  mantleExitSpeed: 3,
  hitStopTicks: 3,

  stickDeadzone: 0.15,
  triggerDeadzone: 0.05,
  reachCurve: 3.15,
  stickActions: 1,
  stickActionThreshold: 0.6,
  padScheme: 6,
  triggerActionThreshold: 0.3,
  stickRunAssist: 0.9,
  stickRunAssistMinX: 0.35,
  barStickJumpDeg: 30,

  failPauseMs: 400,
};

export const TUNING_KEYS = Object.keys(defaultTuning) as (keyof Tuning)[];

/** Groups for the tuning panel. Every key appears in exactly one group. */
export const TUNING_GROUPS: Record<string, (keyof Tuning)[]> = {
  run: [
    "maxRunSpeed",
    "runAccel",
    "startImpulse",
    "stopDecel",
    "turnDecel",
    "skidSpeed",
    "overspeedDecel",
    "leanAccelBonus",
    "leanBrakeBonus",
  ],
  attitude: [
    "inertiaBase",
    "inertiaTucked",
    "inertiaArmsOut",
    "tiltTorque",
    "spinDrag",
    "spinDragGround",
    "rightingGround",
    "rightingAir",
    "barLeanClamp",
    "barLeanMs",
    "releaseSpin",
    "impactPitch",
  ],
  crouch: ["crouchSpeed", "tuckInMs", "tuckOutMs"],
  jump: [
    "gravity",
    "gravityRiseScale",
    "gravityFallScale",
    "terminalVelocity",
    "jumpVelocity",
    "jumpCut",
    "coyoteMs",
    "bufferMs",
    "cornerCorrection",
    "airAccel",
    "airDrag",
  ],
  body: ["capsuleRadius", "standHeight", "crouchDrop", "shoulderBelowTop", "tuckComDrop"],
  arms: [
    "armLength",
    "torsoOffset",
    "torsoOffsetTucked",
    "reachDirection",
    "gripThreshold",
    "gripRelease",
    "reachRadiusBase",
    "reachRadiusPerReach",
    "gripAssist",
    "gripAssistCone",
    "gripAssistRange",
  ],
  swing: [
    "catchRetention",
    "attachTicks",
    "hangOmega",
    "swingGravityScale",
    "swingDamping",
    "swingIdleDamping",
    "swingIdleAfterMs",
    "pumpGain",
    "pumpStick",
    "pumpPerfectMs",
    "pumpGoodMs",
    "pumpBonus",
    "maxSwingAngle",
    "releaseKick",
    "releaseKickTucked",
    "releaseNudge",
    "regrabMs",
  ],
  landing: [
    "rollSpinUntucked",
    "rollSpinTucked",
    "rollRadius",
    "rollGrip",
    "rollMinSpin",
    "rollFinishDeg",
    "rollDropHeight",
    "hardLandingRetain",
    "rollPerfectMin",
    "rollPerfectMax",
    "rollGoodMin",
    "rollGoodMax",
    "rollPerfectBoost",
  ],
  hurdles: [
    "hurdleFlow",
    "hurdleStepHeight",
    "hurdleMaxHeight",
    "vaultLookahead",
    "vaultWindowMs",
    "vaultPerfectLeadMs",
    "vaultPerfectHalfMs",
    "vaultClearance",
    "vaultHipClearance",
    "vaultHandAhead",
    "handOnTop",
    "vaultMountPush",
    "vaultPushMax",
    "vaultSupportMs",
    "vaultTuck",
    "vaultRetainGood",
    "vaultRetainOk",
    "vaultRetainMs",
    "vaultPerfectBoost",
    "hitSpeed",
  ],
  slide: [
    "slideSpeed",
    "slideDecel",
    "slideBrakeBonus",
    "slideJumpVelocity",
    "slideWindowMs",
    "slideWindowLateMs",
    "slidePerfectMs",
    "slidePerfectHalfMs",
    "slidePerfectBoost",
  ],
  ladder: [
    "ladderPerfectDuck",
    "ladderClimbMs",
    "ladderClimbSpeed",
    "ladderPullThreshold",
    "ladderKipTuck",
    "ladderPopPerfectMs",
    "ladderPopEarlyMs",
    "ladderPopLateMs",
    "ladderMinAmplitude",
    "ladderPopLift",
    "ladderPopPerfectScale",
    "ladderPopMs",
    "ladderMissRetention",
    "ladderMissFalls",
  ],
  ledge: [
    "ledgeGap",
    "mantlePullSpeed",
    "mantleClearance",
    "mantlePerfectMs",
    "mantleGoodMs",
    "mantleRetainGood",
    "mantleRetainOk",
    "mantlePerfectBoost",
    "mantleExitSpeed",
  ],
  link: ["linkFlightMaxMs", "hangBreakMs"],
  grades: [
    "catchSetupMs",
    "catchReadyDeg",
    "releasePerfectMin",
    "releasePerfectMax",
    "releaseGoodMin",
    "releaseGoodMax",
    "kickPerfectScale",
    "kickOkScale",
    "pumpLeadMs",
    "hitStopTicks",
  ],
  input: [
    "stickDeadzone",
    "triggerDeadzone",
    "reachCurve",
    "stickActions",
    "stickActionThreshold",
    "padScheme",
    "triggerActionThreshold",
    "stickRunAssist",
    "stickRunAssistMinX",
    "barStickJumpDeg",
  ],
  failure: ["failPauseMs"],
};

export function cloneTuning(t: Tuning): Tuning {
  return { ...t };
}

/** Fill in any missing keys from the defaults (for panels loading older saves). */
export function mergeTuning(base: Tuning, partial: Partial<Tuning> | null | undefined): Tuning {
  const out = cloneTuning(base);
  if (!partial) return out;
  for (const k of TUNING_KEYS) {
    const v = partial[k];
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

/** FNV-1a 32-bit over the canonical JSON of the tuning, as 8 hex characters. */
export function tuningHash(t: Tuning): string {
  let h = 0x811c9dc5;
  for (const k of TUNING_KEYS) {
    // This optional mechanic did not exist in saved tapes before 2026-10-08. Its off setting
    // must retain those exact hashes so old recordings can select their original physics.
    if (k === "slidePerfectBoost" && t[k] === 0) continue;
    if (k === "hurdleFlow" && t[k] === 0) continue;
    if (
      (k === "ladderPerfectDuck" || k === "ladderClimbMs" || k === "ladderClimbSpeed") &&
      t.ladderPerfectDuck === 0
    )
      continue;
    const s = k + "=" + String(t[k]) + ";";
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
  }
  return h.toString(16).padStart(8, "0");
}

function msToTicks(ms: number): number {
  return Math.max(0, Math.round((ms / 1000) * HZ));
}

/** Tuning plus the derived forms step() reads. Recompute whenever the tuning changes. */
export interface CompiledTuning extends Tuning {
  hash: string;
  /** tuckAmount change per second while held / released. */
  tuckInRate: number;
  tuckOutRate: number;
  coyoteTicks: number;
  bufferTicks: number;
  regrabTicks: number;
  swingIdleAfterTicks: number;
  failPauseTicks: number;
  linkFlightMaxTicks: number;
  hangBreakTicks: number;
  catchSetupTicks: number;
  catchReadyRad: number;
  rollFinishRad: number;
  vaultRetainTicks: number;
  vaultSupportTicks: number;
  pumpPerfectTicks: number;
  pumpGoodTicks: number;
  ladderPopTicks: number;
  ladderClimbTicks: number;
  /** Cosine of ladderMinAmplitude: a swing whose energy cannot reach past it cannot lift the bar. */
  cosLadderMinAmplitude: number;
  barLeanClampRad: number;
  /** Bar lean change per second (1 / barLeanMs). */
  barLeanRate: number;
  reachDirRad: number;
  /** Half of the grip assist cone, radians. */
  gripAssistHalfRad: number;
  /** Cosine of the half cone, for a dot-product test. */
  gripAssistCos: number;
  swingG: number;
  /** Cosine of maxSwingAngle, for the pump energy ceiling. */
  cosMaxSwing: number;
  /** Biggest acceleration magnitude any non-impulse tick can apply, for the continuity test. */
  maxAccel: number;
}

export function compileTuning(input: Tuning): CompiledTuning {
  // A missing or non-finite value (a stale save, a module served from an older build) would turn the
  // whole body into NaN; fall back to the default for that key instead.
  let t = input;
  for (const k of TUNING_KEYS) {
    if (typeof input[k] !== "number" || !Number.isFinite(input[k])) {
      if (t === input) t = { ...input };
      t[k] = defaultTuning[k];
    }
  }
  const halfCone = (t.gripAssistCone / 2) * DEG;
  return {
    ...t,
    hash: tuningHash(t),
    tuckInRate: t.tuckInMs > 0 ? 1000 / t.tuckInMs : 1e9,
    tuckOutRate: t.tuckOutMs > 0 ? 1000 / t.tuckOutMs : 1e9,
    coyoteTicks: msToTicks(t.coyoteMs),
    bufferTicks: msToTicks(t.bufferMs),
    regrabTicks: msToTicks(t.regrabMs),
    swingIdleAfterTicks: msToTicks(t.swingIdleAfterMs),
    failPauseTicks: msToTicks(t.failPauseMs),
    linkFlightMaxTicks: msToTicks(t.linkFlightMaxMs),
    hangBreakTicks: msToTicks(t.hangBreakMs),
    catchSetupTicks: msToTicks(t.catchSetupMs),
    catchReadyRad: t.catchReadyDeg * DEG,
    rollFinishRad: t.rollFinishDeg * DEG,
    vaultRetainTicks: msToTicks(t.vaultRetainMs),
    vaultSupportTicks: msToTicks(t.vaultSupportMs),
    pumpPerfectTicks: msToTicks(t.pumpPerfectMs),
    pumpGoodTicks: msToTicks(t.pumpGoodMs),
    ladderPopTicks: Math.max(1, msToTicks(t.ladderPopMs)),
    ladderClimbTicks: Math.max(1, msToTicks(t.ladderClimbMs)),
    cosLadderMinAmplitude: Math.cos(t.ladderMinAmplitude * DEG),
    barLeanClampRad: t.barLeanClamp * DEG,
    barLeanRate: t.barLeanMs > 0 ? 1000 / t.barLeanMs : 1e9,
    reachDirRad: t.reachDirection * DEG,
    gripAssistHalfRad: halfCone,
    gripAssistCos: Math.cos(halfCone),
    swingG: t.gravity * t.swingGravityScale,
    cosMaxSwing: Math.cos(t.maxSwingAngle * DEG),
    maxAccel: Math.max(
      t.runAccel * (1 + t.leanAccelBonus),
      t.stopDecel * (1 + t.leanBrakeBonus),
      t.turnDecel,
      t.gravity * Math.max(t.gravityFallScale, t.gravityRiseScale, 1),
      t.airAccel + t.gravity * t.gravityFallScale,
      t.slideDecel * (1 + t.slideBrakeBonus),
    ),
  };
}
